import { defineRouting } from "next-intl/routing";

/**
 * Locale routing with **translated paths** (no /en/ or /es/ prefix).
 *
 * Each internal route can map to a different external URL per locale,
 * so Spanish visitors see /servicios/alquiler instead of /es/services/rentals.
 * Locale on first visit is detected from the Accept-Language header and
 * persisted in the NEXT_LOCALE cookie; the language switcher updates it.
 *
 * Internal route paths (the keys below) match the directories under
 * `src/app/[locale]/` — those stay in English so file/route layout is
 * stable across translations.
 */
export const routing = defineRouting({
  locales: ["en", "es"],
  defaultLocale: "en",
  localePrefix: "never",
  pathnames: {
    "/": "/",
    "/art": { en: "/art", es: "/arte" },
    "/blog": "/blog",
    "/blog/[slug]": "/blog/[slug]",
    "/contact": { en: "/contact", es: "/contacto" },
    "/faqs": { en: "/faqs", es: "/preguntas" },
    "/privacy": { en: "/privacy", es: "/privacidad" },
    "/projects": { en: "/projects", es: "/proyectos" },
    // Static, so it precedes the dynamic [slug] entry — every "coming soon"
    // placeholder card links here instead of to its own detail page.
    "/projects/coming-soon": {
      en: "/projects/coming-soon",
      es: "/proyectos/proximamente",
    },
    "/projects/[slug]": {
      en: "/projects/[slug]",
      es: "/proyectos/[slug]",
    },
    "/services": { en: "/services", es: "/servicios" },
    "/services/podcasts": {
      en: "/services/podcasts",
      es: "/servicios/podcasts",
    },
    "/services/rentals": {
      en: "/services/rentals",
      es: "/servicios/alquiler",
    },
    "/services/rentals/catalog": {
      en: "/services/rentals/catalog",
      es: "/servicios/alquiler/catalogo",
    },
    "/services/rentals/catalog/[sku]": {
      en: "/services/rentals/catalog/[sku]",
      es: "/servicios/alquiler/catalogo/[sku]",
    },
    "/services/stills": {
      en: "/services/stills",
      es: "/servicios/fotografia",
    },
    "/team": { en: "/team", es: "/equipo" },
  },
});

export type Locale = (typeof routing.locales)[number];
export type Pathname = keyof typeof routing.pathnames;
